/**
 * "Needs you" on Home — the order items appear in and how long a snooze lasts.
 * (F46)
 *
 * The ranking is the server's: `GET /api/home` returns the items already
 * sorted, and a client renders them in that order. It lives here, beside the
 * other hero rules, so it is one table rather than a sort buried in a service.
 *
 * Why this order (highest first), in the three groups Home shows:
 *
 *   Needs an answer — a company waits on a decision only the citizen can make:
 *     1. invitation          — it expires;
 *     2. sponsorship_offer   — the company has already paid, and nothing ever
 *                              expires it (so it never ranks above a deadline);
 *   To do — only the citizen can move these forward:
 *     3. testit_request      — a company is waiting on specific tests;
 *     4. checkup_requested   — a company is waiting on the CheckUp score;
 *     5. profile_incomplete  — the F33 gate refuses a CheckUp score until it is done;
 *     6. task_overdue        — a relocation deadline already missed;
 *     7. checkin_due         — today's check-in, a daily habit;
 *     8. checkpoint_due      — a Care milestone whose date has come;
 *   For your information — nothing to decide:
 *     9. checkup_shared      — the company now sees the score;
 *    10. unread_notifications — things to read.
 *
 * @module heroes/home
 */
import type { HomeNeedsYouItem, HomeNeedsYouKind, HomePhase, HomeSnoozeKind } from '@tonnie/types';

/** Higher ranks first. Every kind has a distinct rank, so the order is total. */
export const HOME_NEEDS_YOU_RANK: Readonly<Record<HomeNeedsYouKind, number>> = {
  invitation: 1000,
  sponsorship_offer: 900,
  testit_request: 800,
  checkup_requested: 700,
  profile_incomplete: 600,
  task_overdue: 500,
  checkin_due: 400,
  checkpoint_due: 300,
  checkup_shared: 200,
  unread_notifications: 100,
};

/** Every kind a snooze request may name — validated server-side against this list. */
export const HOME_SNOOZE_KINDS = [
  'invitation',
  'sponsorship_offer',
  'testit_request',
  'checkup_requested',
  'checkup_shared',
  'profile_incomplete',
  'task_overdue',
  'checkin_due',
  'checkpoint_due',
  'unread_notifications',
] as const satisfies readonly HomeSnoozeKind[];

export function isHomeSnoozeKind(value: unknown): value is HomeSnoozeKind {
  return typeof value === 'string' && (HOME_SNOOZE_KINDS as readonly string[]).includes(value);
}

/**
 * How long "Later" hides an item recorded in `home_feed_snoozes`: three days.
 * Long enough that "not now" is respected past today, short enough that a state
 * which has NOT resolved comes back within the week. An invitation is not here —
 * its window is the invitation defer's own (`INVITATION_DEFER_SNOOZE_DAYS`) —
 * and neither are the dismissals that do not expire
 * (`HOME_DISMISS_FOR_GOOD_KINDS`).
 */
export const HOME_FEED_SNOOZE_DAYS = 3;

/**
 * Items whose "Later" is a dismissal: information, never a request, so bringing
 * it back would only repeat what the citizen has already read —
 * `checkup_shared` says a company now sees the score.
 */
export const HOME_DISMISS_FOR_GOOD_KINDS = [
  'checkup_shared',
] as const satisfies readonly HomeSnoozeKind[];

export function isDismissedForGood(kind: HomeSnoozeKind): boolean {
  return (HOME_DISMISS_FOR_GOOD_KINDS as readonly HomeSnoozeKind[]).includes(kind);
}

/** Sort by rank, highest first. Stable for equal ranks (the input order within a kind). */
export function rankNeedsYou(items: readonly HomeNeedsYouItem[]): HomeNeedsYouItem[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort(
      (a, b) =>
        HOME_NEEDS_YOU_RANK[b.item.kind] - HOME_NEEDS_YOU_RANK[a.item.kind] || a.index - b.index
    )
    .map(({ item }) => item);
}

/** The stable id of an item: its kind and what it is about. */
export function homeNeedsYouId(kind: HomeNeedsYouKind, subjectRef: string): string {
  return `${kind}:${subjectRef}`;
}

/**
 * Whether an item asks for something the path to the score already asks for,
 * on this Home: while the citizen is getting ready, the path's steps ARE the
 * profile gate and the company's request for the CheckUp score, so a client
 * that draws the path does not ask twice (`onPath`). Once the score exists
 * the path is gone, and a request that only waits on consent is never a step.
 */
export function isOnPath(phase: HomePhase, item: HomeNeedsYouItem): boolean {
  if (phase !== 'getting_ready') return false;
  switch (item.kind) {
    case 'profile_incomplete':
      return true;
    case 'checkup_requested':
      return item.awaiting === 'score';
    case 'invitation':
    case 'sponsorship_offer':
    case 'testit_request':
    case 'checkup_shared':
    case 'task_overdue':
    case 'checkin_due':
    case 'checkpoint_due':
    case 'unread_notifications':
      return false;
    default: {
      const unreachable: never = item;
      throw new Error(`unknown Needs-you item ${JSON.stringify(unreachable)}`);
    }
  }
}
